import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { AppState, Linking, Platform, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import RNWebView, { type WebViewNavigation } from 'react-native-webview'
import type { WebViewOpenWindowEvent } from 'react-native-webview/lib/WebViewTypes'
import Loader from './Loader'
import type { UniversalWebViewProps } from './types'

const injectedJSForAppName = 'window.__RN_APP_NAME__ = "nextapp";true;'

function openInBrowser(url: string) {
  // iOS rejects openURL when the app isn't foreground-active, which fires a
  // spurious "Unable to open URL" after a duplicate click callback once the
  // first openURL already backgrounded the app to open the file.
  if (AppState.currentState !== 'active') return
  Linking.openURL(url).catch(error => console.error(error))
}

export default function UniversalRNWebView<MData>({ ref, ...props }: UniversalWebViewProps<MData>) {
  const {
    source,
    style,
    className,
    startInLoadingState = true,
    injectedJavaScript: injectedJavaScriptProp,
    injectedCSS,
    contentInsetAdjustmentBehavior = 'automatic',
    onLoad,
    onError,
    onMessage,
    onContentReady,
    scrollEnabled = true,
    onOpenLink,
  } = props

  // 当提供 onContentReady 时，使用手动 loading 状态
  const useManualLoading = !!onContentReady
  const [isLoading, setIsLoading] = useState(useManualLoading)

  const webviewRef = useRef<any>(null)

  useImperativeHandle(ref, () => ({
    reload: () => {
      webviewRef.current?.reload()
    },
    postMessage: (payload: string | object) => {
      const message = typeof payload === 'string' ? payload : JSON.stringify(payload)
      if (Platform.OS === 'android') {
        // Android's WebView.postMessage dispatches on document (non-bubbling),
        // so it won't reach window.addEventListener listeners in the webview JS.
        webviewRef.current?.injectJavaScript(
          `window.dispatchEvent(new MessageEvent('message',{data:${JSON.stringify(message)}}));true;`
        )
      } else {
        webviewRef.current?.postMessage(message)
      }
    },
    goBack: () => {
      webviewRef.current?.goBack?.()
    },
    goForward: () => {
      webviewRef.current?.goForward?.()
    },
  }))

  const handleShouldStartLoadWithRequest = useCallback(
    (request: WebViewNavigation) => {
      if (request.navigationType === 'click') {
        const result = onOpenLink?.(request.url) ?? 'browser'
        if (result === 'webview') return true
        if (result === 'browser') openInBrowser(request.url)
        return false
      }
      return true
    },
    [onOpenLink]
  )

  const handleOpenWindow = useCallback(
    ({ nativeEvent: { targetUrl } }: WebViewOpenWindowEvent) => {
      const result = onOpenLink?.(targetUrl) ?? 'browser'
      if (result === 'webview') {
        webviewRef.current?.injectJavaScript(`window.location.assign(${JSON.stringify(targetUrl)});true;`)
      } else if (result === 'browser') {
        openInBrowser(targetUrl)
      }
    },
    [onOpenLink]
  )

  const injectedJavaScriptBeforeContentLoaded = useMemo(() => {
    // Lock WebView text size so iOS/Android system font scaling does not affect nextfe pages.
    const baseCSS = 'html{-webkit-text-size-adjust:100%!important;text-size-adjust:100%!important;}'
    const fullCSS = injectedCSS ? `${baseCSS}\n${injectedCSS}` : baseCSS
    const escapedCSS = fullCSS.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n')
    return `
      (function() {
        var css = '${escapedCSS}';
        function injectStyle() {
          var style = document.createElement('style');
          style.textContent = css;
          document.head.appendChild(style);
        }
        if (document.head) injectStyle();
        else document.addEventListener('DOMContentLoaded', injectStyle);
      })();
    `
  }, [injectedCSS])

  const injectedJavaScript = useMemo(
    () => [injectedJavaScriptProp, injectedJSForAppName].filter(Boolean).join('\n'),
    [injectedJavaScriptProp]
  )

  const handleMessage = useCallback(
    (e: { nativeEvent: { data: string } }) => {
      const data = JSON.parse(e.nativeEvent.data)
      // 处理 contentReady 事件
      if (data?.event === 'contentReady') {
        setIsLoading(false)
        onContentReady?.()
        return
      }
      onMessage?.(data)
    },
    [onMessage, onContentReady]
  )

  // 当使用手动 loading 时，不依赖 startInLoadingState
  const effectiveStartInLoadingState = useManualLoading ? false : startInLoadingState

  return (
    <View style={{ flex: 1 }} className={className}>
      {/* The pages set viewport-fit=cover but only pad the bottom, so keep them beside the iPhone Duo side bar */}
      <SafeAreaView edges={['left', 'right']} style={{ flex: 1 }}>
        <RNWebView
          ref={webviewRef}
          source={source}
          onLoadEnd={onLoad}
          onError={onError}
          onMessage={handleMessage}
          injectedJavaScript={injectedJavaScript}
          injectedJavaScriptBeforeContentLoaded={injectedJavaScriptBeforeContentLoaded}
          applicationNameForUserAgent="nextapp"
          style={style}
          allowsBackForwardNavigationGestures
          startInLoadingState={effectiveStartInLoadingState}
          renderLoading={Loader}
          onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
          contentInsetAdjustmentBehavior={contentInsetAdjustmentBehavior}
          scrollEnabled={scrollEnabled}
          webviewDebuggingEnabled={__DEV__}
          {...(Platform.OS === 'android' ? { textZoom: 100 } : null)}
          // Android hands a new window's link to the system browser by itself, never asking
          // onShouldStartLoadWithRequest. iOS goes without it: a click there already reaches that handler.
          {...(Platform.OS === 'android' && onOpenLink ? { onOpenWindow: handleOpenWindow } : null)}
        />
        {useManualLoading && isLoading && <Loader />}
      </SafeAreaView>
    </View>
  )
}
